import type { Player } from "../engine/player";
import type { VendingMachine, VendingMode } from "../engine/things/vendingMachine";
import { Strings } from "../engine/strings/strings";
import { getPickupIconFilenameByKey } from "./pickupIconMap";
import { createOverlay, createButton, IconLoader } from "./dom";
import type { OverlayHandle } from "./dom";
import { playUiSound, UiSound } from "./sound/uiSoundPlayer";
import { VendingViewModel, type BuyTile, type SellRow, type SellSummary } from "./viewModels/vendingViewModel";

const ICON_SIZE = 24;
const GOLD_DELTA_FADE_MS = 1500;

export class VendingMachineOverlay {
	private player: Player;
	private machine: VendingMachine;
	private vm: VendingViewModel;
	private onClose: () => void;
	private onTransaction: () => void;
	private overlay: OverlayHandle | null = null;
	private goldCounterEl: HTMLSpanElement | null = null;
	private contentArea: HTMLDivElement | null = null;
	private tabRow: HTMLDivElement | null = null;
	private activeTab: "buy" | "sell";
	private sellBasket: Map<string, number> = new Map();
	private scrollTop: number = 0;
	private goldDeltaEl: HTMLSpanElement | null = null;
	private goldDeltaAmount: number = 0;
	private goldDeltaExpireTime: number = 0;

	constructor(player: Player, machine: VendingMachine, onClose: () => void, onTransaction: () => void) {
		this.player = player;
		this.machine = machine;
		this.vm = new VendingViewModel(player, machine);
		this.onClose = onClose;
		this.onTransaction = onTransaction;
		this.activeTab = machine.mode === "sell" ? "sell" : "buy";
	}

	show(): void {
		if (this.overlay) return;

		playUiSound(UiSound.CLICK);
		const overlay = createOverlay({ level: 1, onEscape: () => this.dismiss() });
		overlay.element.classList.add("muted");
		overlay.panel.classList.add("vending");

		const canvasHeight = document.getElementById("game")?.clientHeight ?? window.innerHeight;
		overlay.panel.style.height = `${Math.round(canvasHeight * 0.8)}px`;

		overlay.panel.appendChild(this.buildTitleBar());
		overlay.panel.appendChild(this.buildGoldRow());
		if (this.machine.mode === "both") {
			overlay.panel.appendChild(this.buildTabRow());
		}
		overlay.panel.appendChild(this.buildContentArea());

		this.overlay = overlay;
	}

	private buildTitleBar(): HTMLDivElement {
		const titleBar = document.createElement("div");
		titleBar.className = "vending-title-bar";

		const title = document.createElement("div");
		title.className = "vending-title";
		title.textContent = this.machine.getName();

		const closeBtn = document.createElement("button");
		closeBtn.className = "vending-close-btn";
		closeBtn.textContent = "X";
		closeBtn.onclick = () => {
			playUiSound(UiSound.CLICK);
			this.dismiss();
		};

		titleBar.appendChild(title);
		titleBar.appendChild(closeBtn);
		return titleBar;
	}

	private buildGoldRow(): HTMLDivElement {
		const goldRow = document.createElement("div");
		goldRow.className = "vending-gold-row";

		const goldIconContainer = document.createElement("span");
		goldIconContainer.className = "vending-gold-icon";
		this.loadGoldIcon(goldIconContainer);

		const goldLabel = document.createElement("span");
		goldLabel.className = "vending-gold-label";
		goldLabel.textContent = Strings.get("ui_vendGoldLabel") ?? "Gold: ";

		const goldValue = document.createElement("span");
		goldValue.className = "vending-gold-value";
		goldValue.textContent = String(this.player.gold);
		this.goldCounterEl = goldValue;

		const goldDelta = document.createElement("span");
		goldDelta.className = "gold-delta large";
		this.goldDeltaEl = goldDelta;

		goldRow.appendChild(goldIconContainer);
		goldRow.appendChild(goldLabel);
		goldRow.appendChild(goldValue);
		goldRow.appendChild(goldDelta);
		return goldRow;
	}

	private buildTabRow(): HTMLDivElement {
		const mode = this.machine.mode;
		const tabRow = document.createElement("div");
		tabRow.className = "vending-tab-row";

		const buyTab = this.createTabButton(Strings.get("ui_vendBuyTab") ?? "Buy", "buy", mode);
		const sellTab = this.createTabButton(Strings.get("ui_vendSellTab") ?? "Sell", "sell", mode);
		tabRow.appendChild(buyTab);
		tabRow.appendChild(sellTab);
		this.tabRow = tabRow;
		return tabRow;
	}

	private buildContentArea(): HTMLDivElement {
		const contentArea = document.createElement("div");
		contentArea.className = "vending-content";
		this.contentArea = contentArea;
		this.renderContentArea();
		return contentArea;
	}

	dismiss(): void {
		if (this.overlay) {
			this.overlay.close();
		}
		this.overlay = null;
		this.goldCounterEl = null;
		this.goldDeltaEl = null;
		this.contentArea = null;
		this.tabRow = null;
		this.onClose();
	}

	isOpen(): boolean {
		return this.overlay !== null && this.overlay.element.parentNode !== null;
	}

	refresh(): void {
		if (this.goldCounterEl) {
			this.goldCounterEl.textContent = String(this.player.gold);
		}
		this.renderContentArea();
	}

	private createTabButton(label: string, tab: "buy" | "sell", _mode: VendingMode): HTMLButtonElement {
		const btn = document.createElement("button");
		btn.textContent = label;
		btn.dataset.tab = tab;
		btn.className = this.activeTab === tab ? "vending-tab active" : "vending-tab";
		btn.onclick = () => {
			playUiSound(UiSound.CLICK);
			this.activeTab = tab;
			this.scrollTop = 0;
			this.updateTabStyles();
			this.renderContentArea();
		};
		return btn;
	}

	private updateTabStyles(): void {
		if (!this.tabRow) return;
		const buttons = this.tabRow.querySelectorAll("button");
		for (const btn of buttons) {
			const tab = btn.dataset.tab === "buy" ? "buy" : "sell";
			btn.classList.toggle("active", this.activeTab === tab);
		}
	}

	private renderContentArea(): void {
		if (!this.contentArea) return;
		if (this.activeTab === "buy") {
			this.renderBuyPanel(this.contentArea);
		} else {
			this.renderSellPanel(this.contentArea);
		}
	}

	// --- Buy panel ---

	private renderBuyPanel(container: HTMLDivElement): void {
		const savedScroll = this.scrollTop;
		container.textContent = "";
		// Reset flex/overflow that renderSellPanel may have set.
		container.style.textAlign = "left";
		container.style.display = "block";
		container.style.overflowY = "auto";

		container.onscroll = () => { this.scrollTop = container.scrollTop; };

		const tiles = this.vm.computeBuyTiles();
		if (tiles.length === 0) {
			container.style.textAlign = "center";
			container.textContent = Strings.get("ui_vendNoOffers") ?? "No items for sale.";
			return;
		}

		const grid = document.createElement("div");
		grid.className = "vending-buy-grid";

		for (const tile of tiles) {
			grid.appendChild(this.buildOfferTile(tile));
		}

		container.appendChild(grid);

		requestAnimationFrame(() => { container.scrollTop = savedScroll; });
	}

	private buildOfferTile(tileData: BuyTile): HTMLDivElement {
		const disabled = tileData.isSoldOut || tileData.isUnaffordable;

		const tile = document.createElement("div");
		tile.className = disabled ? "vending-tile disabled" : "vending-tile";

		if (!disabled) {
			tile.addEventListener("click", () => this.handleBuyClick(tileData));
		}

		const iconContainer = this.buildIconContainer(tileData.iconFilename);
		tile.appendChild(iconContainer);

		const textCol = document.createElement("div");
		textCol.className = "vending-text-col";

		const nameEl = document.createElement("div");
		nameEl.className = "vending-item-name";
		const packSuffixTemplate = Strings.get("ui_vendPackSuffix") ?? "(pack of {n})";
		const nameText = tileData.packSize > 1
			? `${tileData.displayName} ${packSuffixTemplate.replace("{n}", String(tileData.packSize))}`
			: tileData.displayName;
		nameEl.textContent = nameText;
		nameEl.title = nameText;
		textCol.appendChild(nameEl);

		const detailRow = document.createElement("div");
		detailRow.className = "vending-detail-row";

		const priceEl = document.createElement("span");
		priceEl.className = "vending-price";
		priceEl.textContent = `$${tileData.totalPrice}`;
		detailRow.appendChild(priceEl);

		if (tileData.packSize > 1) {
			const unitPriceEl = document.createElement("span");
			unitPriceEl.className = "vending-unit-price";
			const unit = tileData.unitPrice;
			const unitText = Number.isInteger(unit) ? String(unit) : unit.toFixed(1);
			unitPriceEl.textContent = `($${unitText} ${Strings.get("ui_vendEach") ?? "each"})`;
			detailRow.appendChild(unitPriceEl);
		}

		textCol.appendChild(detailRow);

		const stockEl = document.createElement("div");
		if (tileData.isSoldOut) {
			stockEl.className = "vending-stock sold-out";
			stockEl.textContent = Strings.get("ui_vendSoldOut") ?? "SOLD OUT";
		} else {
			stockEl.className = "vending-stock";
			stockEl.textContent = `${tileData.stockLeft} ${Strings.get("ui_vendLeft") ?? "left"}`;
		}
		textCol.appendChild(stockEl);

		tile.appendChild(textCol);

		return tile;
	}

	private handleBuyClick(tile: BuyTile): void {
		const result = this.vm.commitBuy(tile.entryId);
		if (!result.success) return;

		playUiSound(UiSound.COIN);
		this.triggerGoldDelta(-result.goldSpent);
		this.onTransaction();
		this.refresh();
	}

	private buildIconContainer(filename: string | null): HTMLDivElement {
		const container = document.createElement("div");
		container.className = "vending-icon";
		container.style.width = `${ICON_SIZE}px`;
		container.style.height = `${ICON_SIZE}px`;

		if (!filename) return container;

		const cached = IconLoader.getCachedIcon(filename, ICON_SIZE);
		if (cached) {
			container.appendChild(IconLoader.copyIconCanvas(cached));
		} else {
			IconLoader.loadIcon(filename, ICON_SIZE).then((img) => {
				if (container.isConnected) {
					container.appendChild(IconLoader.copyIconCanvas(img));
				}
			});
		}

		return container;
	}

	private loadGoldIcon(container: HTMLSpanElement): void {
		const filename = getPickupIconFilenameByKey("GoldDrop");
		if (!filename) return;
		IconLoader.loadIcon(filename, ICON_SIZE).then((canvas) => {
			const img = document.createElement("img");
			img.src = canvas.toDataURL();
			img.width = ICON_SIZE;
			img.height = ICON_SIZE;
			container.appendChild(img);
		});
	}

	// --- Sell panel ---

	private renderSellPanel(container: HTMLDivElement): void {
		const savedScroll = this.scrollTop;
		container.textContent = "";
		// Flex column pins the footer while the item list scrolls above.
		container.style.textAlign = "left";
		container.style.display = "flex";
		container.style.flexDirection = "column";
		container.style.overflowY = "hidden";
		container.onscroll = null;

		const rows = this.vm.computeSellRows(this.sellBasket);

		if (rows.length === 0) {
			container.style.textAlign = "center";
			container.style.overflowY = "auto";
			container.textContent = Strings.get("ui_vendNothingToSell") ?? "Nothing to sell.";
			return;
		}

		const scrollArea = document.createElement("div");
		scrollArea.className = "vending-sell-scroll";

		scrollArea.onscroll = () => { this.scrollTop = scrollArea.scrollTop; };

		const list = document.createElement("div");
		list.className = "vending-sell-list";

		for (const row of rows) {
			list.appendChild(this.buildSellRow(row));
		}

		scrollArea.appendChild(list);
		container.appendChild(scrollArea);

		requestAnimationFrame(() => { scrollArea.scrollTop = savedScroll; });

		const summary = this.vm.computeSellSummary(this.sellBasket);
		const footer = document.createElement("div");
		footer.className = "vending-sell-footer";

		const totalBar = document.createElement("div");
		totalBar.className = "vending-sell-total-bar";

		const totalLabel = document.createElement("span");
		totalLabel.className = "vending-sell-total-label";
		totalLabel.textContent = Strings.get("ui_vendReceiveTotal") ?? "You will receive: ";

		const totalValue = document.createElement("span");
		totalValue.className = "vending-sell-total-value";
		totalValue.textContent = `$${summary.totalGold}`;

		totalLabel.appendChild(totalValue);
		totalBar.appendChild(totalLabel);

		const confirmBtn = createButton(
			Strings.get("ui_vendConfirmSale") ?? "Confirm Sale",
			() => this.handleConfirmSale(summary),
			"primary",
		);
		confirmBtn.classList.add("btn-compact");
		confirmBtn.disabled = !summary.canConfirm;
		totalBar.appendChild(confirmBtn);

		footer.appendChild(totalBar);
		container.appendChild(footer);
	}

	private buildSellRow(row: SellRow): HTMLDivElement {
		const el = document.createElement("div");
		el.className = "vending-sell-row";

		const iconContainer = this.buildIconContainer(row.iconFilename);
		el.appendChild(iconContainer);

		const textCol = document.createElement("div");
		textCol.className = "vending-text-col";

		const nameEl = document.createElement("div");
		nameEl.className = "vending-item-name";
		const remaining = row.availableCount - row.basketCount;
		nameEl.textContent = `${row.displayName} (${remaining})`;
		nameEl.title = nameEl.textContent;
		textCol.appendChild(nameEl);

		const priceEl = document.createElement("div");
		priceEl.className = "vending-sell-per-unit";
		priceEl.textContent = `$${row.pricePerUnit} ${Strings.get("ui_vendEach") ?? "each"}`;
		textCol.appendChild(priceEl);

		el.appendChild(textCol);

		const stepper = document.createElement("div");
		stepper.className = "vending-stepper";

		const minusBtn = document.createElement("button");
		minusBtn.className = "stepper-btn";
		minusBtn.textContent = "\u2013";
		minusBtn.disabled = !row.canStepDown;
		minusBtn.onclick = () => {
			if (!row.canStepDown) return;
			playUiSound(UiSound.CLICK);
			this.sellBasket = this.vm.basketStepDown(this.sellBasket, row.itemKey);
			this.renderContentArea();
		};
		stepper.appendChild(minusBtn);

		const countEl = document.createElement("span");
		countEl.className = "vending-stepper-count";
		countEl.textContent = String(row.basketCount);
		stepper.appendChild(countEl);

		const plusBtn = document.createElement("button");
		plusBtn.className = "stepper-btn";
		plusBtn.textContent = "+";
		plusBtn.disabled = !row.canStepUp;
		plusBtn.onclick = () => {
			if (!row.canStepUp) return;
			playUiSound(UiSound.CLICK);
			this.sellBasket = this.vm.basketStepUp(this.sellBasket, row.itemKey);
			this.renderContentArea();
		};
		stepper.appendChild(plusBtn);

		el.appendChild(stepper);

		const subtotal = document.createElement("span");
		subtotal.className = "vending-sell-subtotal";
		subtotal.textContent = row.basketCount > 0 ? `$${row.basketCount * row.pricePerUnit}` : "";
		el.appendChild(subtotal);

		return el;
	}

	private handleConfirmSale(summary: SellSummary): void {
		if (!summary.canConfirm) return;

		const totalGold = this.vm.commitSale(this.sellBasket);
		if (totalGold === 0) return;

		playUiSound(UiSound.COIN);
		this.triggerGoldDelta(totalGold);
		this.onTransaction();

		this.sellBasket = this.vm.basketClear();
		this.refresh();
	}

	private triggerGoldDelta(amount: number): void {
		if (!this.goldDeltaEl || amount === 0) return;
		const now = performance.now();

		if (now < this.goldDeltaExpireTime) {
			this.goldDeltaAmount += amount;
		} else {
			this.goldDeltaAmount = amount;
		}
		this.goldDeltaExpireTime = now + GOLD_DELTA_FADE_MS;

		const sign = this.goldDeltaAmount >= 0 ? "+" : "";
		this.goldDeltaEl.textContent = `${sign}${this.goldDeltaAmount}`;

		this.goldDeltaEl.style.color = this.goldDeltaAmount >= 0 ? "#ffec80" : "#ff6666";

		// Re-trigger CSS animation via remove + forced reflow + re-add.
		this.goldDeltaEl.style.visibility = "visible";
		this.goldDeltaEl.classList.remove("anim-gold-delta-rise");
		void this.goldDeltaEl.offsetWidth;
		this.goldDeltaEl.classList.add("anim-gold-delta-rise");
	}
}
